import React, { useEffect, useState } from 'react';
import Button from '../../ui/Button/Button';
import IconButton from '../../ui/IconButton';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import Dialog from '../../ui/Dialog/Dialog';
import { Field, Input, Textarea } from '../../ui/Field/Field';
import { toast } from '../../ui/feedback';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import { partsFromItems, sameParts, fifoPreview } from '../../services/serviceRecords.logic';
import FifoPartsSection from './FifoPartsSection';
import './ServiceModal.css';

const FORM_ID = 'fsh-service-form';
const NO_START = { parts: [], unknown: [], lineParts: [] };

// `fifo` (the new store, USE_CONTRACTS_V2): the form asks for PARTS and quantities and the engine takes them from the
// invoices oldest first (owner, 28 Sep 2026); off (Firestore, the live site): the batch picker exactly as before.
export default function ServiceModal({ onClose, onSave, vehicle, inventory, editData, serviceRecords = [], isSaving, fifo = USE_CONTRACTS_V2 }) {
    // an edited record's parts as the form starts with them: its lines grouped per part (the lines the page cannot
    // name are kept as saved by the engine and only shown), and how each old line was counted (sent with the save)
    const [fifoStart, setFifoStart] = useState(() => (fifo && editData ? partsFromItems(editData.items, inventory) : NO_START));
    const [partsTouched, setPartsTouched] = useState(false);
    const [formData, setFormData] = useState(editData ? {
        date: editData.date,
        vehicleId: editData.vehicleId,
        currentKm: editData.currentKm,
        ...(fifo ? { parts: fifoStart.parts } : { items: editData.items }),
        notes: editData.notes
    } : {
        date: new Date().toISOString().split('T')[0],
        vehicleId: '',
        currentKm: '',
        ...(fifo ? { parts: [] } : { items: [] }),
        notes: ''
    });

    // The page reads the inventory in its own request, so an edit can open before it answers: every old line then
    // looked unknown and the parts started empty, and a part added next dropped the old lines (review 28 Sep 2026).
    // Until the user touches the parts, they are worked out again from the inventory as it arrives or is read again.
    useEffect(() => {
        if (!fifo || !editData || partsTouched) return;
        const start = partsFromItems(editData.items, inventory);
        setFifoStart(start);
        setFormData(prev => ({ ...prev, parts: start.parts }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventory]);

    const [selectedItem, setSelectedItem] = useState('');
    const [selectedQuantity, setSelectedQuantity] = useState('');
    const [itemSearch, setItemSearch] = useState('');
    const [showItemDropdown, setShowItemDropdown] = useState(false);

    const [vehicleSearch, setVehicleSearch] = useState(editData ?
        `${editData.vehicleId} - ${vehicle.find(v => v.plate_number === editData.vehicleId)?.make} ${vehicle.find(v => v.plate_number === editData.vehicleId)?.model}`
        : '');
    const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
    const [duplicateWarningData, setDuplicateWarningData] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddItem = () => {
        if (!selectedItem || !selectedQuantity) return;

        const item = inventory.find(i => i.id === selectedItem);
        if (!item) return;

        if (parseFloat(selectedQuantity) > item.quantity) {
            toast.error(`Available quantity: ${item.quantity} ${item.unit} only!`);
            return;
        }

        addItemToList(item, parseFloat(selectedQuantity));
    };

    const addItemToList = (item, quantity) => {
        const newItem = {
            itemId: item.id,
            quantity: quantity,
            unitCost: item.unitCost || 0,
            invoiceNumber: item.invoiceNumber || ''
        };

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));

        setSelectedItem('');
        setSelectedQuantity('');
        setItemSearch('');
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // What the page is given to save. The store: the parts (left out when an edit did not change them - the saved
    // lines then stay exactly as they are, the record keeps its total and nothing can have moved, so there is no
    // preview to compare: a legacy total that is not its lines' rounded sum said "stock changed" - review 28 Sep
    // 2026); an edit's parts go with how the form counted the old lines (savedLineParts - the engine refuses when it
    // counts them otherwise now); and the preview's total, to say so when the engine's saved cost differs. No items
    // and no total - the engine writes both. Firestore: the batch lines and their total, as before.
    const savePayload = () => {
        if (!fifo) return { ...formData, totalCost: formData.items.reduce((sum, item) => sum + (item.quantity * item.unitCost), 0) };
        if (editData && sameParts(fifoStart.parts, formData.parts)) return { ...formData, parts: undefined };
        const preview = fifoPreview(inventory, formData.parts, editData?.items || []);
        return {
            ...formData,
            ...(editData ? { savedLineParts: fifoStart.lineParts } : {}),
            previewTotal: preview.total,
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (fifo ? formData.parts.length === 0 && fifoStart.unknown.length === 0 : formData.items.length === 0) {
            toast.error('Please add at least one item');
            return;
        }

        // Check for recent service on same vehicle
        const vehicleRecords = serviceRecords.filter(r => r.vehicleId === formData.vehicleId);
        if (vehicleRecords.length > 0 && !editData) {
            const lastService = vehicleRecords.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
            const kmDiff = parseInt(formData.currentKm) - parseInt(lastService.currentKm);
            const daysDiff = Math.floor((new Date(formData.date) - new Date(lastService.date)) / (1000 * 60 * 60 * 24));

            const [year, month, day] = lastService.date.split('-');
            const formattedDate = `${day}/${month}/${year}`;

            setDuplicateWarningData({
                lastService: formattedDate,
                lastKm: lastService.currentKm,
                daysSince: daysDiff,
                kmDiff: kmDiff
            });
            setShowDuplicateWarning(true);
            return;
        }
        onSave(savePayload());
    };

    const handleConfirmDuplicate = () => {
        setShowDuplicateWarning(false);
        onSave(savePayload());
    };

    const vehicleMatches = vehicle
        .filter(v =>
            v.plate_number.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
            `${v.make} ${v.model}`.toLowerCase().includes(vehicleSearch.toLowerCase())
        );

    const activeItem = inventory.find(i => i.id === selectedItem);
    const isQtyLocked = activeItem && (activeItem.name.toLowerCase().includes('filter') || activeItem.unit === 'Piece');

    return (
        <Dialog
            open
            onClose={isSaving ? undefined : onClose}
            title={editData ? 'Edit Service' : 'Record Service'}
            size="xl"
            closeOnOverlay={false}
            footer={(
                <>
                    <Button type="button" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="submit" form={FORM_ID} variant="primary" isLoading={isSaving}>
                        {editData ? 'Update Service' : 'Save Service'}
                    </Button>
                </>
            )}
        >
            <form id={FORM_ID} onSubmit={handleSubmit} className="service-form">
                <div className="ds-field">
                    <label className="ds-field__label" htmlFor="fsh-service-vehicle">
                        Select Vehicle<span className="ds-field__required" aria-hidden="true">*</span>
                    </label>
                    <div className="vehicle-search-container">
                        <Input
                            id="fsh-service-vehicle"
                            type="text"
                            autoComplete="off"
                            value={vehicleSearch}
                            onChange={(e) => {
                                setVehicleSearch(e.target.value);
                                setShowVehicleDropdown(true);
                                setFormData(prev => ({ ...prev, vehicleId: '' }));
                            }}
                            onFocus={() => setShowVehicleDropdown(true)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (vehicleMatches.length > 0) {
                                        const firstVehicle = vehicleMatches[0];
                                        setFormData(prev => ({ ...prev, vehicleId: firstVehicle.plate_number }));
                                        setVehicleSearch(`${firstVehicle.plate_number} - ${firstVehicle.make} ${firstVehicle.model}`);
                                        setShowVehicleDropdown(false);
                                    }
                                }
                            }}
                            placeholder="Search by plate number..."
                            required={!formData.vehicleId}
                        />
                        {showVehicleDropdown && vehicleSearch && (
                            <div className="vehicle-dropdown">
                                {vehicleMatches
                                    .slice(0, 10)
                                    .map(v => (
                                        <button
                                            type="button"
                                            key={v.plate_number}
                                            className="vehicle-option"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, vehicleId: v.plate_number }));
                                                setVehicleSearch(`${v.plate_number} - ${v.make} ${v.model}`);
                                                setShowVehicleDropdown(false);
                                            }}
                                        >
                                            <strong>{v.plate_number}</strong> - {v.make} {v.model}
                                        </button>
                                    ))
                                }
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-row">
                    <Field label="Date" required>
                        <Input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                        />
                    </Field>
                    <Field label="Current Kilometers" required>
                        <Input
                            type="number"
                            name="currentKm"
                            value={formData.currentKm}
                            onChange={handleChange}
                            placeholder="e.g: 10000"
                        />
                    </Field>
                </div>

                {fifo ? (
                    <FifoPartsSection
                        inventory={inventory}
                        parts={formData.parts}
                        onChange={(parts) => {
                            setPartsTouched(true);
                            setFormData(prev => ({ ...prev, parts }));
                        }}
                        savedItems={editData?.items || []}
                    />
                ) : (
                <section className="items-section" aria-labelledby="fsh-items-title">
                    <h4 id="fsh-items-title">Items Used</h4>

                    <div className="add-item-row">
                        <div className="item-search-container">
                            <Input
                                type="text"
                                aria-label="Search part by name or PN"
                                autoComplete="off"
                                value={itemSearch}
                                onChange={(e) => {
                                    setItemSearch(e.target.value);
                                    setShowItemDropdown(true);
                                    setSelectedItem('');
                                }}
                                onFocus={() => setShowItemDropdown(true)}
                                placeholder="Search part by name or PN..."
                            />
                            {showItemDropdown && itemSearch && (
                                <div className="item-dropdown">
                                    {inventory
                                        .filter(item => item.quantity > 0)
                                        .filter(item => {
                                            const search = itemSearch.toLowerCase();
                                            const partNum = (item.partNumber || '').toLowerCase();
                                            const name = (item.name || '').toLowerCase();
                                            return name.includes(search) || partNum.includes(search);
                                        })
                                        .slice(0, 8)
                                        .map(item => {
                                            const partNum = item.partNumber || '';
                                            return (
                                                <button
                                                    type="button"
                                                    key={item.id}
                                                    className="item-option"
                                                    onClick={() => {
                                                        setSelectedItem(item.id);
                                                        setItemSearch(`${item.name} ${partNum ? `(${partNum})` : ''}`);
                                                        setShowItemDropdown(false);

                                                        // Auto-lock for Filter/Piece
                                                        const isFilter = item.name.toLowerCase().includes('filter');
                                                        const isPiece = item.unit === 'Piece';
                                                        if (isFilter || isPiece) {
                                                            setSelectedQuantity('1');
                                                        } else {
                                                            setSelectedQuantity('');
                                                        }
                                                    }}
                                                >
                                                    <span className="item-option-main">
                                                        <strong>{item.name}</strong>
                                                        {partNum && <span className="item-option-pn">{partNum}</span>}
                                                    </span>
                                                    <span className="item-option-details">
                                                        Qty: {item.quantity} {item.unit} | Cost: {item.unitCost} {item.invoiceNumber ? `| INV: ${item.invoiceNumber}` : ''}
                                                    </span>
                                                </button>
                                            );
                                        })
                                    }
                                </div>
                            )}
                        </div>

                        <Input
                            type="number"
                            aria-label="Quantity"
                            value={selectedQuantity}
                            onChange={(e) => setSelectedQuantity(e.target.value)}
                            placeholder="Qty"
                            className="quantity-input"
                            step="0.1"
                            disabled={isQtyLocked}
                        />

                        <Button type="button" onClick={handleAddItem} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                            Add
                        </Button>
                    </div>

                    {formData.items.length === 0 ? (
                        <p className="items-empty">No items added yet.</p>
                    ) : (
                        <ul className="items-list">
                            {formData.items.map((item, index) => {
                                const invItem = inventory.find(i => i.id === item.itemId);
                                const partNum = invItem?.partNumber || invItem?.PartNumber || invItem?.part_number || invItem?.['Part Number'] || '';
                                return (
                                    <li key={index} className="item-row">
                                        <span className="item-row__name">
                                            {invItem?.name}{' '}
                                            {partNum
                                                ? <span className="item-row__pn">({partNum})</span>
                                                : <span className="item-row__missing">(Empty PN)</span>}
                                        </span>
                                        <span className="item-row__qty">
                                            ({item.quantity}{invItem?.unit === 'Liter' ? 'L' : (invItem?.unit === 'Piece' ? 'Pcs' : (invItem?.unit ? `${invItem.unit}` : ''))})
                                        </span>
                                        {item.invoiceNumber && <span className="item-row__inv">INV: {item.invoiceNumber}</span>}
                                        <IconButton
                                            icon={ICONS.close}
                                            label={`Remove ${invItem?.name || 'item'}`}
                                            size="xs"
                                            tone="danger"
                                            onClick={() => handleRemoveItem(index)}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
                )}

                <Field label="Notes">
                    <Textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Any additional notes..."
                    />
                </Field>

                <div className="info-box">
                    <Icon glyph={ICONS.calendar} size="sm" />
                    <p>Next service will be at: <strong>{parseInt(formData.currentKm || 0) + 10000} km</strong> or after <strong>6 months</strong> whichever comes first</p>
                </div>
            </form>

            <Dialog
                open={Boolean(showDuplicateWarning && duplicateWarningData)}
                onClose={() => setShowDuplicateWarning(false)}
                role="alertdialog"
                title="Duplicate Service Warning"
                description="This vehicle had service before!"
                size="sm"
                footer={(
                    <>
                        <Button type="button" onClick={() => setShowDuplicateWarning(false)} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button type="button" variant="primary" onClick={handleConfirmDuplicate} isLoading={isSaving}>
                            Continue
                        </Button>
                    </>
                )}
            >
                {duplicateWarningData && (
                    <>
                        <dl className="ds-kv mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                            <dt>Last Service</dt>
                            <dd>{duplicateWarningData.lastService}</dd>
                            <dt>Last KM</dt>
                            <dd>{duplicateWarningData.lastKm} km</dd>
                            <dt>Days Since</dt>
                            <dd>{duplicateWarningData.daysSince} days</dd>
                            <dt>KM Difference</dt>
                            <dd>{duplicateWarningData.kmDiff} km</dd>
                        </dl>
                        <p className="text-sm text-gray-700">Do you want to continue?</p>
                    </>
                )}
            </Dialog>
        </Dialog>
    );
}
